// ============================================================
// O DASHBOARD DE GESTÃO DE CONTAS — o contrato (Story 38.8, Epic 38)
//
// `GET /api/account-management/dashboard?period=6m|12m&customerId=` devolve a
// carteira de quem o abre: os clientes de que é gestor da conta, as operações de
// que é Delivery Manager, ou todos os clientes ativos, para o admin. As definições
// de cada número vivem em `apps/api/src/lib/account-dashboard.ts`; aqui fica só a
// forma, que a API e a tela partilham.
//
// Os dias são `AAAA-MM-DD` em São Paulo, e os meses `AAAA-MM`. Os trimestres são
// `AAAA-Qn`, como em `allocation_reviews.quarter`.
// ============================================================
import { z } from 'zod'

import type { Tendencia } from './allocation-reviews.ts'

export const ACCOUNT_DASHBOARD_PERIODS = ['6m', '12m'] as const
export type AccountDashboardPeriod = (typeof ACCOUNT_DASHBOARD_PERIODS)[number]

export const AccountDashboardQuerySchema = z.object({
  period: z.enum(ACCOUNT_DASHBOARD_PERIODS).default('6m'),
  customerId: z.string().min(1).optional(),
})
export type AccountDashboardQuery = z.infer<typeof AccountDashboardQuerySchema>

/** Um ponto de uma série mensal. `null` quando não há valor nesse mês. */
export interface MonthPoint {
  month: string
  value: number | null
}

/** As faixas da distribuição, pela ordem em que a tela as mostra. */
export const SCORE_BANDS = ['9-10', '8-9', '7-8', '6-7', 'lt6', 'none'] as const
export type ScoreBand = (typeof SCORE_BANDS)[number]

export type AttentionItem =
  | {
      kind: 'score_drop'
      allocationId: string
      professionalId: string
      name: string
      customerName: string
      from: number
      to: number
    }
  | {
      kind: 'overdue_actions'
      allocationId: string
      professionalId: string
      name: string
      customerName: string
      count: number
      oldestDueOn: string
    }
  | {
      kind: 'opportunity_unpresented'
      opportunityId: string
      title: string
      customerName: string
      daysOpen: number
    }
  | {
      kind: 'overdue_review'
      allocationId: string
      professionalId: string
      name: string
      customerName: string
      quarter: string
    }

export type Movement =
  | {
      kind: 'review_received'
      on: string
      professionalId: string
      name: string
      quarter: string
      score: number
    }
  | {
      kind: 'allocation_started'
      on: string
      professionalId: string
      name: string
      roleTitle: string
      customerName: string
    }
  | {
      kind: 'allocation_ended'
      on: string
      professionalId: string
      name: string
      customerName: string
    }
  | {
      kind: 'application_created'
      on: string
      applicationId: string
      name: string
      opportunityTitle: string
    }

export interface AccountDashboard {
  period: AccountDashboardPeriod
  /** A carteira inteira, por nome — também com o filtro de cliente. */
  customers: Array<{ id: string; name: string }>
  kpis: {
    allocated: { value: number; previous: number; series: MonthPoint[] }
    score: { value: number | null; previous: number | null; scored: number; series: MonthPoint[] }
    turnover: {
      value: number | null
      previous: number | null
      ended: number
      endedPerMonth: MonthPoint[]
    }
    overdueReviews: {
      value: number
      quarter: string
      dueOn: string
      pastDue: boolean
      eligible: number
    }
    overdueActions: { value: number; open: number }
    openOpportunities: { value: number; openedThisMonth: number; openedPerMonth: MonthPoint[] }
  }
  distribution: Array<{ band: ScoreBand; count: number }>
  oldestOpenOpportunities: Array<{
    id: string
    title: string
    customerName: string
    activeApplications: number
    presented: number
    daysOpen: number
  }>
  highlights: Array<{
    allocationId: string
    professionalId: string
    name: string
    roleTitle: string
    customerName: string
    score: number
    trend: Tendencia | null
  }>
  attention: { items: AttentionItem[]; omitted: number }
  movements: Movement[]
}
